import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title, 
  description, 
  keywords,
  image,
  url,
  type = 'website',
  structuredData
}) => {
  const siteTitle = 'EventConnect';
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  const defaultDescription = 'EventConnect - Die Plattform für Event-Dienstleister in Deutschland. Finde oder biete Event-Dienstleistungen von DJ über Fotograf bis Catering.';
  const metaDescription = description || defaultDescription;
  
  // Use environment variable or fallback to window.location.origin for production
  const siteUrl = process.env.REACT_APP_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://www.evconn.de');
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  const defaultImage = `${siteUrl}/og-image.jpg`; // Default OG image
  const metaImage = image || defaultImage;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:site_name" content={siteTitle} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Helper function to generate structured data for provider profiles
export const generateProviderSchema = (provider) => {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": provider.company_name,
    "description": provider.short_description,
    "image": provider.images?.map(img => `${process.env.REACT_APP_BACKEND_URL}/uploads/${img}`),
    "address": {
      "@type": "PostalAddress",
      "addressLocality": provider.location_city,
      "postalCode": provider.location_zip,
      "addressCountry": "DE"
    },
    "priceRange": provider.price_range,
    ...(provider.website_link && { "url": provider.website_link }),
    ...(provider.contact_email && { "email": provider.contact_email }),
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "127"
    }
  };
};

// Helper function for search page
export const generateSearchSchema = () => {
  const siteUrl = process.env.REACT_APP_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://eventconnect.de');
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "EventConnect",
    "url": siteUrl,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${siteUrl}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };
};

// Helper function for organization
export const generateOrganizationSchema = () => {
  const siteUrl = process.env.REACT_APP_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://eventconnect.de');
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "EventConnect",
    "url": siteUrl,
    "logo": `${siteUrl}/logo.png`,
    "description": "Die Plattform für Event-Dienstleister in Deutschland",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer support",
      "areaServed": "DE",
      "availableLanguage": "de"
    },
    "sameAs": [
      "https://www.facebook.com/eventconnect",
      "https://www.instagram.com/eventconnect",
      "https://www.linkedin.com/company/eventconnect"
    ]
  };
};

export default SEO;
